import React from 'react'
import { Box, Button, Checkbox, Combobox, Icon, Input } from '@node-space/ui-components'
import type {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form'
import type { BankDetailsFormReturn } from 'pages/Contacts/hooks/useBankDetailsForm'
import type { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressTypes, type FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import { useTranslation } from 'react-i18next'
import { AddressDetails } from './AddressDetails'

type USDBankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  bankDetailsForm: BankDetailsFormReturn
}

export const USDBankDetails = ({
  formFieldLabels,
  formValues,
  errors,
  clearErrors,
  register,
  setError,
  setValue,
  bankDetailsForm,
}: USDBankDetailsProps) => {
  const { t } = useTranslation()

  const {
    isInternational,
    isLocalOnly,
    isAccountNumberAndBankCodeInline,
    isAccountTypeRequired,
    isValidAccountNumber,
    isValidBankCode,
    isBankDetailsValidationLoading,
    isValidatingRoutingNumber,
    isValidatingScan,
    isValidatingBic,
    countriesLoading,
    accountTypeOptions,
    internationalCountryList,
    onFieldUpdate,
    onAccountTypeChange,
    handleAccountNumberOnBlur,
    handleBankCodeOnBlur,
    handleAccountNumberConfirmOnBlur,
    onInternationalCountrySelection,
  } = bankDetailsForm

  const { accountNumber, bankCode, bankCountryCode } = formValues || {}

  const showMainDetails = isLocalOnly || (isInternational && !!bankCountryCode)
  const showSwiftCode = isInternational
  const showBankCode = isLocalOnly || (isInternational && bankCountryCode === 'US')

  const handleIntermediaryBankDetailsChange = (checked: boolean): void => {
    setValue('isIntermediaryBankDetailsRequired', checked)
  }

  return (
    <Box flexCol width="full" gapY={16} paddingT={12}>
      {isInternational && (
        <Box>
          <Combobox
            {...register('bankCountryCode')}
            key={formValues?.bankCountryCode}
            label={t('country')}
            id={t('bankCountryCode')}
            selectPlaceholder={t('searchOrSelectACountry')}
            items={internationalCountryList}
            onChange={onInternationalCountrySelection}
            loading={countriesLoading}
            defaultValue={formValues?.bankCountryCode}
            errorText={errors?.bankCountryCode?.message}
          />
        </Box>
      )}

      {showMainDetails && (
        <Box
          gapX={12}
          gapY={16}
          width="full"
          flexCol
        >
          <Box
            {...(isAccountNumberAndBankCodeInline && {
              flexSize: 'auto',
              width: 'full',
              gapX: 12,
            })}
          >
            <Input
              {...register('accountNumber')}
              name="accountNumber"
              testId="accountNumber"
              label={formFieldLabels?.accountNumber}
              placeholder={formFieldLabels?.accountNumberPlaceholder}
              errorText={errors?.accountNumber?.message}
              error={!!errors?.accountNumber?.message}
              onBlur={handleAccountNumberOnBlur}
              onChange={onFieldUpdate('accountNumber')}
              loading={isBankDetailsValidationLoading}
              IconButton={
                isValidAccountNumber &&
                accountNumber && <Button variant="ghost" iconElement={<Icon name="Check" />} />
              }
              iconPosition="right"
            />
          </Box>

          {isLocalOnly && (
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <Input
                {...register('accountNumberConfirm')}
                name="accountNumberConfirm"
                testId="accountNumberConfirm"
                label={t('formFields.confirmAccountNumber')}
                placeholder={t('formFields.placeholder.confirmAccountNumber')}
                errorText={errors?.accountNumberConfirm?.message}
                error={!!errors?.accountNumberConfirm?.message}
                onChange={onFieldUpdate('accountNumberConfirm')}
                onBlur={handleAccountNumberConfirmOnBlur}
              />
            </Box>
          )}

          {showBankCode && (
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <Input
                {...register('bankCode')}
                name="bankCode"
                testId="bankCode"
                label={formFieldLabels.bankCode}
                placeholder={formFieldLabels?.bankCodePlaceholder}
                errorText={errors?.bankCode?.message}
                error={!!errors?.bankCode?.message}
                loading={isValidatingRoutingNumber || isValidatingScan || isValidatingBic}
                onChange={onFieldUpdate('bankCode')}
                onBlur={handleBankCodeOnBlur}
                IconButton={
                  isValidBankCode &&
                  bankCode && <Button variant="ghost" iconElement={<Icon name="Check" />} />
                }
                iconPosition="right"
              />
            </Box>
          )}

          {showSwiftCode && (
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <Input
                {...register('swiftCode')}
                name="swiftCode"
                testId="swiftCode"
                label={t('formFields.swiftCode')}
                placeholder={t('formFields.placeholder.swiftCode')}
                errorText={errors?.swiftCode?.message}
                error={!!errors?.swiftCode?.message}
                onChange={onFieldUpdate('swiftCode')}
              />
            </Box>
          )}

          {isAccountTypeRequired && (
            <Combobox
              {...register('accountType')}
              key={formValues?.accountType}
              id="accountType"
              testId="accountType"
              label={t('formFields.accountType')}
              items={accountTypeOptions}
              errorText={errors?.accountType?.message}
              defaultValue={formValues?.accountType}
              onChange={onAccountTypeChange}
            />
          )}

          {isInternational && (
            <>

              {bankCountryCode && (
                <>
                  <Box>
                    <Input
                      {...register('bankName')}
                      name="bankName"
                      testId="bankName"
                      label={t('beneficiarysBankName')}
                      placeholder={t('enterBankName')}
                      errorText={errors?.bankName?.message}
                      error={!!errors?.bankName?.message}
                      onChange={onFieldUpdate('bankName')}
                    />
                  </Box>

                  <AddressDetails
                    formValues={formValues}
                    errors={errors}
                    type={AddressTypes.BANK}
                    clearErrors={clearErrors}
                    register={register}
                    setError={setError}
                    setValue={setValue}
                  />
                </>
              )}
            </>
          )}

          {isInternational && (
            <Box paddingT={24}>
              <Checkbox
                {...register('isIntermediaryBankDetailsRequired')}
                id="beneficiary-address-copy-details-checkbox"
                name="beneficiary-address-copy-details-checkbox"
                testId="beneficiary-address-copy-details-checkbox"
                onCheckedChange={handleIntermediaryBankDetailsChange}
                label={t('contacts.intermediaryBankDetailsLabel')}
                checked={formValues?.isIntermediaryBankDetailsRequired}
              >
                {t('contacts.intermediaryBankDetailsDescription')}
              </Checkbox>
            </Box>
          )}
      </Box>
      )}
    </Box>
  )
}

